import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useSWR from "swr";
import api from "@/lib/api";
import { formatIDR, formatDate, terbilang } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Printer, ChevronLeft, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const fetcher = (url) => api.get(url).then((r) => r.data);

const MONTHS_ID = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

function formatIndoDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso.length <= 10 ? iso + "T00:00:00" : iso);
  return `${d.getDate()} ${MONTHS_ID[d.getMonth()]} ${d.getFullYear()}`;
}

function monthsBetween(startISO, endISO) {
  if (!startISO || !endISO) return 0;
  const s = new Date(startISO);
  const e = new Date(endISO);
  return (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
}

function contractNumber(id, startISO) {
  const short = (id || "").replace(/-/g, "").slice(0, 6).toUpperCase();
  const d = startISO ? new Date(startISO) : new Date();
  const roman = ["I","II","III","IV","V","VI","VII","VIII","IX","X","XI","XII"][d.getMonth()];
  return `KTR/${short}/${roman}/${d.getFullYear()}`;
}

export default function ContractPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: contract, error } = useSWR(id ? `/contracts/${id}` : null, fetcher);
  const { data: unit } = useSWR(contract ? `/units/${contract.unit_id}` : null, fetcher);
  const { data: tenant } = useSWR(contract ? `/tenants/${contract.tenant_id}` : null, fetcher);

  const durationMonths = useMemo(
    () => contract ? monthsBetween(contract.start_date, contract.end_date) : 0,
    [contract]
  );
  const contractNo = useMemo(() => contract ? contractNumber(contract.id, contract.start_date) : "", [contract]);

  if (error) return <div className="text-sm text-destructive">Gagal memuat kontrak.</div>;
  if (!contract || !unit || !tenant) return <div className="text-sm text-muted-foreground p-6">Memuat...</div>;

  const lateFeeLine = contract.late_fee_type === "fixed_daily"
    ? `Rp ${Number(contract.late_fee_value).toLocaleString("id-ID")} (${terbilang(contract.late_fee_value)}) untuk setiap hari keterlambatan`
    : contract.late_fee_type === "percentage_daily"
    ? `${contract.late_fee_value}% dari nilai tagihan untuk setiap hari keterlambatan`
    : "Tidak dikenakan denda keterlambatan";

  return (
    <div className="mx-auto max-w-3xl" data-testid="contract-print-page">
      <div className="no-print mb-4 flex items-center justify-between">
        <Button variant="ghost" size="sm" className="gap-1 -ml-2" onClick={() => navigate(-1)}>
          <ChevronLeft className="h-4 w-4" /> Kembali
        </Button>
        <Button className="gap-2" onClick={() => window.print()} data-testid="print-contract-button">
          <Printer className="h-4 w-4" /> Cetak / Simpan PDF
        </Button>
      </div>

      <div className="print-container bg-card border border-border/60 shadow-sm rounded-lg p-8 sm:p-12 font-sans text-[13.5px] leading-relaxed">
        {/* Header */}
        <header className="text-center border-b border-border/60 pb-5">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground grid place-items-center">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="text-left leading-tight">
              <div className="font-display text-base font-semibold tracking-tight">Aplikasi Kontrakan Surbakti</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Sistem Aplikasi Kontrakan Surbakti</div>
            </div>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight uppercase">
            Surat Perjanjian Sewa-Menyewa
          </h1>
          <div className="font-mono text-xs text-muted-foreground mt-2">Nomor: {contractNo}</div>
        </header>

        {/* Preamble */}
        <section className="pt-6 space-y-4 text-justify">
          <p>
            Pada hari ini, <strong>{formatIndoDate(contract.created_at || contract.start_date)}</strong>, yang bertanda
            tangan di bawah ini:
          </p>

          <div className="pl-4">
            <div className="grid grid-cols-[110px_10px_1fr] gap-y-1">
              <div>Nama</div><div>:</div><div className="font-medium">{user?.name || "—"}</div>
              <div>Jabatan</div><div>:</div><div>{user?.role === "owner" ? "Pemilik Kontrakan" : "Pengelola / Admin"}</div>
              <div>Email</div><div>:</div><div>{user?.email || "—"}</div>
            </div>
            <p className="mt-2 italic">Selanjutnya disebut sebagai <strong>PIHAK PERTAMA</strong>.</p>
          </div>

          <div className="pl-4">
            <div className="grid grid-cols-[110px_10px_1fr] gap-y-1">
              <div>Nama</div><div>:</div><div className="font-medium">{tenant.name}</div>
              <div>No. Identitas</div><div>:</div><div>{tenant.id_number || "—"}</div>
              <div>No. Telepon</div><div>:</div><div>{tenant.phone || "—"}</div>
              <div>Alamat</div><div>:</div><div>{tenant.address || "—"}</div>
            </div>
            <p className="mt-2 italic">Selanjutnya disebut sebagai <strong>PIHAK KEDUA</strong>.</p>
          </div>

          <p>
            <strong>PIHAK PERTAMA</strong> dan <strong>PIHAK KEDUA</strong> secara bersama-sama sepakat untuk
            mengadakan Perjanjian Sewa-Menyewa dengan ketentuan-ketentuan sebagai berikut:
          </p>
        </section>

        {/* Pasal-Pasal */}
        <section className="pt-4 space-y-4 text-justify">
          <Pasal no="1" title="OBJEK SEWA">
            <p>
              PIHAK PERTAMA menyewakan kepada PIHAK KEDUA sebuah unit kontrakan dengan rincian:
            </p>
            <div className="mt-2 pl-4 grid grid-cols-[130px_10px_1fr] gap-y-1">
              <div>Kode Unit</div><div>:</div><div className="font-medium">{unit.code}</div>
              <div>Nama Unit</div><div>:</div><div>{unit.name}</div>
              <div>Alamat</div><div>:</div><div>{unit.address || "—"}</div>
            </div>
          </Pasal>

          <Pasal no="2" title="JANGKA WAKTU SEWA">
            <p>
              Jangka waktu sewa disepakati selama{" "}
              <strong>{durationMonths} ({terbilang(durationMonths).replace(" rupiah", "")}) bulan</strong>,
              terhitung sejak <strong>{formatIndoDate(contract.start_date)}</strong> sampai dengan{" "}
              <strong>{formatIndoDate(contract.end_date)}</strong>.
            </p>
          </Pasal>

          <Pasal no="3" title="HARGA SEWA DAN CARA PEMBAYARAN">
            <p>
              Harga sewa disepakati sebesar <strong>{formatIDR(contract.monthly_rent)}</strong>{" "}
              (<em className="first-letter:uppercase">{terbilang(contract.monthly_rent)}</em>) per bulan.
              Pembayaran dilakukan setiap tanggal <strong>{contract.billing_day}</strong> setiap bulannya,
              secara tunai atau transfer ke rekening yang ditunjuk PIHAK PERTAMA.
            </p>
          </Pasal>

          {Number(contract.deposit) > 0 && (
            <Pasal no="4" title="UANG JAMINAN (DEPOSIT)">
              <p>
                PIHAK KEDUA menyerahkan uang jaminan sebesar <strong>{formatIDR(contract.deposit)}</strong>{" "}
                (<em className="first-letter:uppercase">{terbilang(contract.deposit)}</em>) kepada PIHAK PERTAMA
                pada saat kontrak ini ditandatangani. Uang jaminan akan dikembalikan pada akhir masa sewa
                setelah dikurangi biaya kerusakan atau tunggakan (jika ada).
              </p>
            </Pasal>
          )}

          <Pasal no={Number(contract.deposit) > 0 ? "5" : "4"} title="DENDA KETERLAMBATAN">
            <p>
              Apabila PIHAK KEDUA terlambat melakukan pembayaran sewa, dikenakan denda sebesar{" "}
              <strong>{lateFeeLine}</strong>, dihitung sejak tanggal jatuh tempo hingga tanggal pelunasan.
            </p>
          </Pasal>

          <Pasal no={Number(contract.deposit) > 0 ? "6" : "5"} title="KEWAJIBAN PIHAK KEDUA">
            <ol className="list-decimal pl-6 space-y-1">
              <li>Membayar sewa tepat waktu sesuai ketentuan Pasal 3.</li>
              <li>Menjaga kebersihan, kerapian, dan keutuhan unit beserta fasilitasnya.</li>
              <li>Membayar sendiri biaya pemakaian listrik, air, dan biaya lain yang timbul selama masa sewa (kecuali diperjanjikan lain).</li>
              <li>Tidak menyewakan kembali/menyerahkan unit kepada pihak lain tanpa persetujuan tertulis dari PIHAK PERTAMA.</li>
              <li>Tidak menggunakan unit untuk kegiatan yang bertentangan dengan hukum, ketertiban umum, atau kesusilaan.</li>
              <li>Bertanggung jawab atas kerusakan yang timbul akibat kelalaian PIHAK KEDUA.</li>
            </ol>
          </Pasal>

          <Pasal no={Number(contract.deposit) > 0 ? "7" : "6"} title="KEWAJIBAN PIHAK PERTAMA">
            <ol className="list-decimal pl-6 space-y-1">
              <li>Menyerahkan unit dalam kondisi baik dan layak huni pada saat kontrak dimulai.</li>
              <li>Tidak mengganggu ketentraman PIHAK KEDUA selama masa sewa.</li>
              <li>Melakukan perbaikan atas kerusakan struktural yang bukan akibat kelalaian PIHAK KEDUA.</li>
            </ol>
          </Pasal>

          <Pasal no={Number(contract.deposit) > 0 ? "8" : "7"} title="PENGAKHIRAN KONTRAK">
            <p>
              Kontrak ini berakhir dengan sendirinya pada tanggal berakhir masa sewa. Perpanjangan hanya dapat
              dilakukan atas dasar kesepakatan tertulis kedua belah pihak. PIHAK PERTAMA berhak memutuskan
              kontrak lebih awal apabila PIHAK KEDUA melanggar ketentuan dalam perjanjian ini.
            </p>
          </Pasal>

          <Pasal no={Number(contract.deposit) > 0 ? "9" : "8"} title="PENUTUP">
            <p>
              Segala sesuatu yang belum diatur dalam perjanjian ini akan diselesaikan secara musyawarah untuk
              mufakat. Apabila tidak tercapai kesepakatan, kedua belah pihak sepakat menyelesaikannya sesuai
              ketentuan hukum yang berlaku di Republik Indonesia.
            </p>
            <p className="mt-2">
              Demikian perjanjian ini dibuat dalam rangkap 2 (dua) bermeterai cukup, ditandatangani kedua
              belah pihak dalam keadaan sehat jasmani dan rohani, tanpa paksaan dari pihak manapun.
            </p>
          </Pasal>
        </section>

        {/* Signatures */}
        <section className="grid grid-cols-2 gap-8 pt-10">
          <div className="text-center">
            <div className="text-sm">PIHAK PERTAMA,</div>
            <div className="h-24 mt-2 border-b border-dashed border-border/70" />
            <div className="mt-1 font-medium">{user?.name || "—"}</div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              {user?.role === "owner" ? "Pemilik" : "Pengelola"}
            </div>
          </div>
          <div className="text-center">
            <div className="text-sm">PIHAK KEDUA,</div>
            <div className="h-24 mt-2 border-b border-dashed border-border/70" />
            <div className="mt-1 font-medium">{tenant.name}</div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Penyewa</div>
          </div>
        </section>

        <footer className="mt-10 pt-4 border-t border-border/60 flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <span>Dokumen kontrak {contractNo}</span>
          <span>Aplikasi Kontrakan Surbakti · {new Date().getFullYear()}</span>
        </footer>
      </div>
    </div>
  );
}

function Pasal({ no, title, children }) {
  return (
    <div>
      <div className="font-display font-semibold text-sm uppercase tracking-wide">
        Pasal {no} — {title}
      </div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
